import * as React from "react";
import { FocusLayer, ShimLayer, Container, CTA, LayerWrapper, Text, TokenTextAppearance, TextTag, } from "@volkswagen-onehub/components-core";
import { StyledTable, StyledTd, StyledTh } from "../Table";
import styled, { css } from "styled-components";
export const StyledTr = styled.tr `
  ${(props) => props.noChanges &&
    css `
      color: gray;
      font-size: 12px;
    `};
`;
export const FeatureAppConfirmationLayer = ({ headline, handleCancel, handleConfirm, changes, }) => {
    return (React.createElement(React.Fragment, null,
        React.createElement(LayerWrapper, { contentCentered: true },
            React.createElement(ShimLayer, { key: "shim" }),
            React.createElement(FocusLayer, { key: "layer", active: true, size: "C" },
                React.createElement(Container, { gutter: "static400", horizontalAlign: "center", padding: "static450", verticalAlign: "center", wrap: "always", stretchContent: true },
                    React.createElement(Container, { gutter: "static300", wrap: "always" },
                        React.createElement(Text, { bold: true, appearance: TokenTextAppearance.headline300, tag: TextTag.h2 }, headline),
                        React.createElement(StyledTable, null,
                            React.createElement("thead", null,
                                React.createElement("tr", null,
                                    React.createElement(StyledTh, null),
                                    React.createElement(StyledTh, null),
                                    React.createElement(StyledTh, null, "before"),
                                    React.createElement(StyledTh, null, "after"))),
                            React.createElement("tbody", null, changes.map((c) => {
                                const { oldCfEnabled, newCfEnabled, hasAnyHash, cfChanged, } = getCfDiff(c.oldVersion, c.newVersion);
                                return (React.createElement(React.Fragment, { key: c.name },
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.version ?? undefined, newValue: c.newVersion.version ?? undefined, propertyName: "version", head: React.createElement(StyledTh, { wordBreak: true, rowSpan: 9 }, c.name) }),
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.baseUrl ?? undefined, newValue: c.newVersion.baseUrl ?? undefined, propertyName: "base url" }),
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.url, newValue: c.newVersion.url, propertyName: "client path" }),
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.ssrUrl ?? undefined, newValue: c.newVersion.ssrUrl ?? undefined, propertyName: "server path" }),
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.schema ?? undefined, newValue: c.newVersion.schema ?? undefined, propertyName: "schema" }),
                                    React.createElement(PropertyRow, { oldValue: c.oldVersion?.team ?? undefined, newValue: c.newVersion.team ?? undefined, propertyName: "team" }),
                                    React.createElement(PropertyRow, { oldValue: formatCss(c.oldVersion?.css), newValue: formatCss(c.newVersion.css), propertyName: "css" }),
                                    React.createElement(PropertyRow, { oldValue: typeof oldCfEnabled === "boolean"
                                            ? oldCfEnabled
                                                ? "Content Fragment enabled"
                                                : "Content Fragment disabled"
                                            : undefined, newValue: typeof newCfEnabled === "boolean"
                                            ? newCfEnabled
                                                ? "Content Fragment enabled"
                                                : "Content Fragment disabled"
                                            : undefined, propertyName: "CF model enabled" }),
                                    React.createElement(StyledTr, { noChanges: !cfChanged },
                                        React.createElement(StyledTd, { wordBreak: true, noWrap: true }, "CF model changed"),
                                        React.createElement(StyledTd, { colSpan: 2, style: { textAlign: "center" } }, isCfChanged(hasAnyHash, cfChanged)))));
                            }))),
                        React.createElement(Container, { stretchContent: true, gutter: "dynamic0300" },
                            React.createElement(CTA, { onClick: handleCancel, tag: "button", emphasis: "secondary", stretchContent: true }, "cancel"),
                            React.createElement(CTA, { onClick: handleConfirm, tag: "button", emphasis: "primary", stretchContent: true }, "confirm"))))))));
};
function PropertyChangeCells(props) {
    const { oldValue, newValue } = props;
    if (!hasChanges(oldValue, newValue)) {
        return (React.createElement(React.Fragment, null,
            React.createElement(StyledTd, null, !isEmpty(oldValue) ? getDisplayValue(oldValue) : React.createElement("i", null, "(empty)")),
            React.createElement(StyledTd, null,
                React.createElement("i", null, "(no changes)"))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTd, null, !isEmpty(oldValue) ? getDisplayValue(oldValue) : React.createElement("i", null, "(empty)")),
        React.createElement(StyledTd, null, !isEmpty(newValue) ? getDisplayValue(newValue) : React.createElement("i", null, "(empty)"))));
}
function PropertyRow(props) {
    const { propertyName, oldValue, newValue, head } = props;
    return (React.createElement(StyledTr, { noChanges: !hasChanges(oldValue, newValue) },
        head,
        React.createElement(StyledTd, { wordBreak: true, noWrap: true }, propertyName),
        React.createElement(PropertyChangeCells, { oldValue: oldValue, newValue: newValue })));
}
function getDisplayValue(value) {
    return value && value.length > 100 ? React.createElement("i", null, "(too long)") : value;
}
function hasChanges(oldValue, newValue) {
    const oldEmpty = isEmpty(oldValue);
    const newEmpty = isEmpty(newValue);
    if (oldEmpty && newEmpty) {
        return false;
    }
    return oldValue !== newValue;
}
function isEmpty(oldValue) {
    return (oldValue === null || typeof oldValue === "undefined" || oldValue === "null");
}
function getCfDiff(oldVersion, newVersion) {
    const oldCfEnabled = typeof oldVersion?.contentFragmentEnabled === "boolean"
        ? oldVersion.contentFragmentEnabled
        : undefined;
    const newCfEnabled = typeof newVersion.contentFragmentEnabled === "boolean"
        ? newVersion.contentFragmentEnabled
        : undefined;
    const oldHash = oldVersion?.contentFragmentModelsHash ?? undefined;
    const newHash = newVersion.contentFragmentModelsHash ?? undefined;
    const hasOldHash = !isEmpty(oldHash);
    const hasNewHash = !isEmpty(newHash);
    return {
        oldCfEnabled,
        newCfEnabled,
        hasAnyHash: hasOldHash || hasNewHash,
        cfChanged: hasOldHash && hasNewHash && oldHash !== newHash,
    };
}
function isCfChanged(hasAnyHash, cfChanged) {
    if (!hasAnyHash)
        return React.createElement("i", null, "Content Fragment model not present");
    if (cfChanged) {
        return React.createElement("i", null, "Content Fragment model changed (too long to display)");
    }
    return React.createElement("i", null, "No Content Fragment model changes");
}
function formatCss(css) {
    if (!css || css.length === 0)
        return undefined;
    return css
        .map((entry) => entry.media ? `${entry.href} (${entry.media})` : entry.href)
        .join(", ");
}
